/**
 * Build-time / run-time logger configuration.
 *
 * • Works in Node, Storybook and browser tests without leaking secrets.
 * • Keeps the env-access helper in one place (DRY, SoC).
 * • Type-safe and guarded against circular-import timing.
 */
/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */
/** True when executed inside Storybook (regardless of Jest) */
const win = globalThis.window;
const storybookFromWindow = typeof win !== 'undefined' && typeof win?.__STORYBOOK_ADDONS__ !== 'undefined';
const storybookFromGlobal = typeof globalThis.__STORYBOOK_ADDONS__ !== 'undefined';
const isStorybook = storybookFromWindow || storybookFromGlobal;
/** Safe env reader that also works in browser-only tests */
const env = (key, fallback = '') => typeof process !== 'undefined' && process.env ? (process.env[key] ?? fallback) : fallback;
const resolveSentryEnvironment = () => {
    if (isStorybook) {
        return 'storybook';
    }
    const sentryEnvironment = env('SENTRY_ENVIRONMENT').trim();
    return sentryEnvironment || env('NODE_ENV', 'development');
};
/* -------------------------------------------------------------------------- */
/* Factory                                                                    */
/* -------------------------------------------------------------------------- */
export const buildLoggerConfig = () => ({
    level: (isStorybook ? 'warn' : env('LOG_LEVEL', 'info')),
    elasticsearch: {
        enabled: !isStorybook && Boolean(env('ELASTICSEARCH_URL')),
        node: env('ELASTICSEARCH_URL', 'http://localhost:9200'),
        indexPrefix: env('INDEX_PREFIX', 'app-logs'),
        apiKey: env('ELASTICSEARCH_API_KEY'),
    },
    sentry: {
        dsn: env('SENTRY_DSN'),
        environment: resolveSentryEnvironment(),
    },
    serviceName: env('SERVICE_NAME', isStorybook ? 'storybook-service' : 'my-service'),
});
/**
 * Shared logger configuration instance used across the logger package.
 * Building it once avoids repeating environment lookups.
 */
export const loggerConfig = buildLoggerConfig();
/* -------------------------------------------------------------------------- */
/* Diagnose early (Node-only)                                                 */
/* -------------------------------------------------------------------------- */
// Intentionally no eager side effects here.
// This module is imported in both browser and Node contexts; avoid dynamic imports that confuse bundlers.
//# sourceMappingURL=loggerConfig.js.map